import { useState, useRef, useCallback, useEffect } from 'react';

interface PracticeModeProps {
  pdfUrl?: string;
  pdfFileName?: string;
  audioUrl?: string;
  audioFileName?: string;
  showRecordButton?: boolean;
  onRecordClick?: () => void;
  recordingSlot?: React.ReactNode;
}

const MIN_RATE = 0.5;
const MAX_RATE = 1.5;
const RATE_STEP = 0.05;

function formatTime(seconds: number): string {
  if (!isFinite(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function AudioPlayer({ url, fileName }: { url: string; fileName?: string }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onDuration = () => setDuration(audio.duration || 0);
    const onRateChange = () => setPlaybackRate(audio.playbackRate);

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('durationchange', onDuration);
    audio.addEventListener('ratechange', onRateChange);

    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('durationchange', onDuration);
      audio.removeEventListener('ratechange', onRateChange);
    };
  }, []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
  }, []);

  const changeRate = useCallback((delta: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newRate = Math.round((audio.playbackRate + delta) * 100) / 100;
    audio.playbackRate = Math.max(MIN_RATE, Math.min(MAX_RATE, newRate));
  }, []);

  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * duration;
  }, [duration]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === ' ' || e.key.toLowerCase() === 'p') {
        e.preventDefault();
        togglePlay();
      }
      if (e.key.toLowerCase() === 'o') {
        e.preventDefault();
        stop();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [togglePlay, stop]);

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const ratePercent = Math.round(playbackRate * 100);

  return (
    <div>
      <audio ref={audioRef} src={url} preload="auto" />
      <div className="playback-bar" style={{ flexWrap: 'wrap' }}>
        {/* Play/Stop */}
        <div className="playback-controls">
          <button
            className="btn btn-icon btn-icon-sm"
            onClick={togglePlay}
            title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
            style={{
              background: isPlaying ? 'var(--color-warning)' : 'var(--color-accent)',
              color: '#fff',
              border: 'none',
            }}
          >
            {isPlaying ? '⏸' : '▶'}
          </button>
          {(isPlaying || currentTime > 0) && (
            <button
              className="btn btn-icon btn-icon-sm btn-ghost"
              onClick={stop}
              title="Stop (O)"
            >
              ⏹
            </button>
          )}
        </div>

        {/* Timeline */}
        <div style={{ flex: 1, minWidth: 100, display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
          <span className="playback-time" style={{ minWidth: 40 }}>{formatTime(currentTime)}</span>
          <div className="playback-timeline" onClick={handleSeek}>
            <div className="playback-timeline-progress" style={{ width: `${progressPercent}%` }} />
            <div className="playback-timeline-scrubber" style={{ left: `${progressPercent}%` }} />
          </div>
          <span className="playback-time" style={{ minWidth: 40 }}>{formatTime(duration)}</span>
        </div>

        {/* Speed */}
        <div className="playback-tempo">
          <button className="btn btn-icon btn-icon-sm btn-ghost" onClick={() => changeRate(-RATE_STEP)} disabled={playbackRate <= MIN_RATE} title="Slower">−</button>
          <span style={{
            minWidth: 48,
            textAlign: 'center',
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            fontWeight: playbackRate < 1 ? 700 : 400,
            color: playbackRate < 1 ? 'var(--color-accent)' : 'var(--color-text-secondary)',
          }}>
            {ratePercent}%
          </span>
          <button className="btn btn-icon btn-icon-sm btn-ghost" onClick={() => changeRate(RATE_STEP)} disabled={playbackRate >= MAX_RATE} title="Faster">+</button>
        </div>

        {/* Download + Label */}
        <a
          href={url}
          download={fileName || 'melody.wav'}
          className="btn btn-icon btn-icon-sm btn-ghost"
          title="Download audio"
          onClick={(e) => e.stopPropagation()}
        >
          ⬇
        </a>
      </div>
    </div>
  );
}

function PdfViewer({ url, fileName }: { url: string; fileName?: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Listen for fullscreen changes
  useEffect(() => {
    const onChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current.requestFullscreen();
    }
  }, []);

  // Focus the iframe on mount so keyboard nav works immediately
  const handleIframeLoad = useCallback(() => {
    iframeRef.current?.focus();
  }, []);

  const focusPdf = useCallback(() => {
    iframeRef.current?.focus();
  }, []);

  return (
    <div ref={containerRef} style={{ display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 'var(--radius-lg)', overflow: 'hidden', border: '1px solid var(--color-border)' }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'var(--space-sm) var(--space-md)',
        background: 'var(--color-bg-elevated)',
        borderBottom: '1px solid var(--color-border)',
      }}>
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
          {fileName || 'Chord Sheet'}
        </span>
        <div style={{ display: 'flex', gap: 'var(--space-xs)', alignItems: 'center' }}>
          <span
            style={{ fontSize: 11, color: 'var(--color-text-muted)', cursor: 'pointer' }}
            onClick={focusPdf}
            title="Click to focus the PDF, then use arrow keys to navigate"
          >
            Click PDF → use ↑↓ keys
          </span>
          <span style={{ width: 1, height: 16, background: 'var(--color-border)', margin: '0 4px' }} />
          <a
            href={url}
            download={fileName || 'chords.pdf'}
            className="btn btn-sm btn-ghost"
            title="Download PDF"
            onClick={(e) => e.stopPropagation()}
          >
            ⬇ Download
          </a>
          <button
            className="btn btn-sm btn-ghost"
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? '⊠ Exit' : '⛶ Fullscreen'}
          </button>
        </div>
      </div>
      {/* PDF via iframe — gets keyboard focus for native arrow key page navigation */}
      <iframe
        ref={iframeRef}
        src={url}
        onLoad={handleIframeLoad}
        title="Chord Sheet PDF"
        style={{
          width: '100%',
          height: isFullscreen ? 'calc(100vh - 44px)' : '70vh',
          minHeight: 400,
          border: 'none',
        }}
      />
    </div>
  );
}

export function PracticeMode({ pdfUrl, pdfFileName, audioUrl, audioFileName, showRecordButton, onRecordClick, recordingSlot }: PracticeModeProps) {
  if (!pdfUrl && !audioUrl) {
    return (
      <div style={{
        padding: 'var(--space-2xl)',
        textAlign: 'center',
        color: 'var(--color-text-muted)',
        background: 'var(--color-bg-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
      }}>
        No practice materials available for this lesson yet.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}>
      {/* Audio player (sticky at top when scrolling PDF) */}
      {audioUrl && (
        <div style={{ position: 'sticky', top: 0, zIndex: 10 }}>
          <AudioPlayer url={audioUrl} fileName={audioFileName} />
        </div>
      )}

      {/* PDF viewer */}
      {pdfUrl && <PdfViewer url={pdfUrl} fileName={pdfFileName} />}

      {/* Audio-only fallback message */}
      {audioUrl && !pdfUrl && (
        <div style={{
          padding: 'var(--space-lg)',
          textAlign: 'center',
          color: 'var(--color-text-muted)',
          background: 'var(--color-bg-card)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          fontSize: 14,
        }}>
          Play the melody track above and practice along with your guitar.
          {audioFileName && <span style={{ display: 'block', fontSize: 12, marginTop: 4 }}>{audioFileName}</span>}
        </div>
      )}

      {/* Record button */}
      {showRecordButton && onRecordClick && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'var(--space-sm)' }}>
          <button
            className="btn btn-sm"
            onClick={onRecordClick}
            style={{
              background: 'var(--color-incorrect)',
              color: '#fff',
              border: 'none',
            }}
          >
            ⏺ Record My Performance
          </button>
        </div>
      )}

      {/* Recording overlay slot */}
      {recordingSlot}
    </div>
  );
}
